import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import Image from '../../../components/AppImage';

const EcoGuide = ({ isOpen, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const categories = [
    { id: 'all', name: 'All Products', icon: 'Leaf' },
    { id: 'cleaners', name: 'All-Purpose Cleaners', icon: 'Spray' },
    { id: 'kitchen', name: 'Kitchen', icon: 'ChefHat' },
    { id: 'bathroom', name: 'Bathroom', icon: 'Bath' },
    { id: 'laundry', name: 'Laundry', icon: 'Shirt' },
    { id: 'diy', name: 'DIY Recipes', icon: 'Beaker' }
  ];

  const ecoProducts = [
    {
      id: 1,
      name: 'White Vinegar Multi-Purpose Cleaner',
      category: 'cleaners',
      type: 'diy',
      rating: 4.8,
      difficulty: 'Easy',
      time: '5 minutes',
      image: 'https://images.pexels.com/photos/4099238/pexels-photo-4099238.jpeg',
      description: 'Natural, non-toxic cleaner perfect for most surfaces. Safe for families and pets.',
      ingredients: ['1 cup white vinegar', '1 cup water', '10 drops essential oil (optional)'],
      instructions: [
        'Mix equal parts white vinegar and water in a spray bottle',
        'Add essential oil for pleasant scent if desired',
        'Shake well before each use',
        'Spray on surface and wipe with microfiber cloth'
      ],
      benefits: ['Non-toxic', 'Antimicrobial', 'Removes soap scum', 'Deodorizes'],
      surfaces: ['Glass', 'Countertops', 'Appliances', 'Mirrors'],
      avoid: ['Natural stone', 'Marble', 'Granite']
    },
    {
      id: 2,
      name: 'Baking Soda Scrub',
      category: 'cleaners',
      type: 'diy',
      rating: 4.9,
      difficulty: 'Easy',
      time: '2 minutes',
      image: 'https://images.pexels.com/photos/6197119/pexels-photo-6197119.jpeg',
      description: 'Gentle abrasive cleaner that removes stains and odors naturally.',
      ingredients: ['1/2 cup baking soda', '2-3 tablespoons water', '1 tablespoon dish soap (optional)'],
      instructions: [
        'Mix baking soda with water to form a paste',
        'Add dish soap for extra cleaning power',
        'Apply to surface and scrub gently',
        'Rinse thoroughly with water'
      ],
      benefits: ['Deodorizes', 'Gentle abrasive', 'Removes stains', 'Safe for food surfaces'],
      surfaces: ['Sinks', 'Bathtubs', 'Ovens', 'Cutting boards'],
      avoid: ['Aluminum', 'Delicate fabrics']
    },
    {
      id: 3,
      name: 'Lemon Fresh Kitchen Cleaner',
      category: 'kitchen',
      type: 'diy',
      rating: 4.7,
      difficulty: 'Easy',
      time: '10 minutes',
      image: 'https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg',
      description: 'Citrus-powered cleaner that cuts through grease and leaves a fresh scent.',
      ingredients: ['2 lemons (juiced)', '1/2 cup water', '1 tablespoon olive oil', '1 teaspoon salt'],
      instructions: [
        'Combine lemon juice and water in spray bottle',
        'For wood surfaces, mix lemon juice with olive oil',
        'Add salt for extra scrubbing power on tough stains',
        'Spray and wipe with clean cloth'
      ],
      benefits: ['Natural degreaser', 'Fresh scent', 'Antibacterial', 'Removes mineral deposits'],
      surfaces: ['Cutting boards', 'Countertops', 'Appliances', 'Wood surfaces'],
      avoid: ['Natural stone', 'Marble']
    },
    {
      id: 4,
      name: 'Castile Soap Bathroom Cleaner',
      category: 'bathroom',
      type: 'diy',
      rating: 4.6,
      difficulty: 'Easy',
      time: '5 minutes',
      image: 'https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg',
      description: 'Gentle yet effective cleaner for all bathroom surfaces.',
      ingredients: ['1/4 cup liquid castile soap', '1 cup water', '1 tablespoon white vinegar', '10 drops tea tree oil'],
      instructions: [
        'Mix all ingredients in a spray bottle',
        'Shake well before each use',
        'Spray on surfaces and let sit for 2-3 minutes',
        'Scrub if needed and rinse with water'
      ],
      benefits: ['Plant-based', 'Antimicrobial', 'Biodegradable', 'Gentle on skin'],
      surfaces: ['Toilets', 'Showers', 'Sinks', 'Tiles'],
      avoid: ['Use sparingly on glass to avoid streaking']
    },
    {
      id: 5,
      name: 'Natural Laundry Detergent',
      category: 'laundry',
      type: 'diy',
      rating: 4.5,
      difficulty: 'Medium',
      time: '15 minutes',
      image: 'https://images.pexels.com/photos/5591663/pexels-photo-5591663.jpeg',
      description: 'Homemade laundry detergent that\'s gentle on clothes and the environment.',
      ingredients: ['1 cup washing soda', '1 cup borax', '1 bar castile soap (grated)', '20 drops essential oil'],
      instructions: [
        'Grate the castile soap bar finely',
        'Mix all dry ingredients in a large bowl',
        'Add essential oil and mix thoroughly',
        'Store in airtight container',
        'Use 1-2 tablespoons per load'
      ],
      benefits: ['Cost-effective', 'No harsh chemicals', 'Customizable scent', 'Concentrated formula'],
      surfaces: ['All fabric types', 'Colors and whites'],
      avoid: ['Very hard water (may need extra rinse)']
    },
    {
      id: 6,
      name: 'Seventh Generation All-Purpose Cleaner',
      category: 'cleaners',
      type: 'commercial',
      rating: 4.4,
      difficulty: 'Ready-to-use',
      time: 'Instant',
      image: 'https://images.pexels.com/photos/4239119/pexels-photo-4239119.jpeg',
      description: 'Plant-based cleaner that\'s EPA Safer Choice certified.',
      ingredients: ['Plant-based surfactants', 'Essential oils', 'Water'],
      instructions: [
        'Spray directly on surface',
        'Wipe with clean cloth',
        'No rinsing required for most surfaces'
      ],
      benefits: ['EPA Safer Choice', 'Plant-based', 'No synthetic fragrances', 'Biodegradable'],
      surfaces: ['Most household surfaces', 'Sealed wood', 'Plastic', 'Metal'],
      avoid: ['Natural stone', 'Unfinished wood']
    }
  ];

  const filteredProducts = ecoProducts?.filter(product => {
    const matchesCategory = activeCategory === 'all' || product?.category === activeCategory;
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const closeProductDetail = () => {
    setSelectedProduct(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Leaf" size={24} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Eco-Friendly Cleaning Guide
              </h2>
              <p className="text-sm text-text-secondary">
                Discover natural cleaning solutions that are safe for your family and the environment
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close eco guide"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {selectedProduct ? (
          /* Product Detail View */
          (<div className="flex flex-col h-full max-h-[calc(90vh-80px)]">
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                {/* Product Header */}
                <div className="flex items-start space-x-4 mb-6">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeProductDetail}
                    aria-label="Back to guide"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </Button>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-2xl font-bold text-text-primary">
                        {selectedProduct?.name}
                      </h3>
                      <div className="flex items-center space-x-1">
                        <Icon name="Star" size={16} color="var(--color-warning)" />
                        <span className="text-sm font-medium">{selectedProduct?.rating}</span>
                      </div>
                    </div>
                    <p className="text-text-secondary mb-4">{selectedProduct?.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                      <span className="flex items-center space-x-1">
                        <Icon name="Clock" size={14} />
                        <span>{selectedProduct?.time}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="BarChart3" size={14} />
                        <span>{selectedProduct?.difficulty}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Tag" size={14} />
                        <span className="capitalize">{selectedProduct?.type}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Product Image */}
                  <div className="lg:col-span-1">
                    <div className="w-full h-64 rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={selectedProduct?.image}
                        alt={selectedProduct?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Ingredients */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="List" size={18} />
                        <span>Ingredients</span>
                      </h4>
                      <ul className="space-y-1">
                        {selectedProduct?.ingredients?.map((ingredient, index) => (
                          <li key={index} className="text-sm text-text-secondary flex items-center space-x-2">
                            <Icon name="Dot" size={12} />
                            <span>{ingredient}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Instructions */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="FileText" size={18} />
                        <span>Instructions</span>
                      </h4>
                      <ol className="space-y-2">
                        {selectedProduct?.instructions?.map((step, index) => (
                          <li key={index} className="text-sm text-text-secondary flex items-start space-x-3">
                            <span className="flex-shrink-0 w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Benefits */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="CheckCircle" size={18} />
                        <span>Benefits</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct?.benefits?.map((benefit, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-accent/10 text-accent text-xs font-medium rounded-full"
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Safe Surfaces */}
                    <div>
                      <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Shield" size={18} />
                        <span>Safe for These Surfaces</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct?.surfaces?.map((surface, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full"
                          >
                            {surface}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Avoid */}
                    {selectedProduct?.avoid && selectedProduct?.avoid?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                          <Icon name="AlertTriangle" size={18} />
                          <span>Avoid or Use Caution</span>
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProduct?.avoid?.map((item, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>)
        ) : (
          /* Product Grid View */
          (<div className="flex flex-col h-full max-h-[calc(90vh-80px)]">
            {/* Search and Filter */}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder="Search eco-friendly products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex space-x-2 overflow-x-auto">
                  {categories?.map((category) => (
                    <Button
                      key={category?.id}
                      variant={activeCategory === category?.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveCategory(category?.id)}
                      iconName={category?.icon}
                      iconPosition="left"
                      className="whitespace-nowrap"
                    >
                      {category?.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            {/* Products Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts?.map((product) => (
                  <div
                    key={product?.id}
                    className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-elevation-2 transition-all duration-200 cursor-pointer"
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="h-48 overflow-hidden bg-muted">
                      <Image
                        src={product?.image}
                        alt={product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-text-primary line-clamp-2">
                          {product?.name}
                        </h3>
                        <div className="flex items-center space-x-1 ml-2">
                          <Icon name="Star" size={14} color="var(--color-warning)" />
                          <span className="text-xs font-medium">{product?.rating}</span>
                        </div>
                      </div>
                      <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                        {product?.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-text-secondary">
                        <span className="flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{product?.time}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="BarChart3" size={12} />
                          <span>{product?.difficulty}</span>
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          product?.type === 'diy' ?'bg-primary/10 text-primary' :'bg-accent/10 text-accent'
                        }`}>
                          {product?.type?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts?.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-text-secondary)" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No products found
                  </h3>
                  <p className="text-text-secondary">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </div>)
        )}
      </div>
    </div>
  );
};

export default EcoGuide;